import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RefreshCw, Search } from 'lucide-react';

const FILTERS = [
  { id: 'all', label: 'Alle' },
  { id: 'incident', label: 'Incidents' },
  { id: 'troubleshoot', label: 'Troubleshoot' },
  { id: 'config', label: 'Config-Changes' },
  { id: 'unban', label: 'Unban' },
];

const formatGermanDateTime = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return 'Ungültiges Datum';
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return 'Fehler bei Datum';
  }
};

const typeBadge = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'incident') return { label: 'Incident', cls: 'bg-red-500/10 text-red-400 border-0' };
  if (t === 'troubleshoot') return { label: 'Troubleshoot', cls: 'bg-orange-500/10 text-orange-400 border-0' };
  if (t === 'config_snapshot') return { label: 'Config Snapshot', cls: 'bg-emerald-500/10 text-emerald-400 border-0' };
  if (t === 'config_rollback') return { label: 'Config Rollback', cls: 'bg-yellow-500/10 text-yellow-300 border-0' };
  if (t === 'unban') return { label: 'Unban', cls: 'bg-blue-500/10 text-blue-400 border-0' };
  return { label: t || 'Event', cls: 'bg-white/5 text-[#a1a1aa] border-0' };
};

const markerColorByType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'incident') return 'bg-red-400';
  if (t === 'troubleshoot') return 'bg-orange-400';
  if (t === 'config_snapshot') return 'bg-emerald-400';
  if (t === 'config_rollback') return 'bg-yellow-300';
  if (t === 'unban') return 'bg-blue-400';
  return 'bg-[#a1a1aa]';
};

const itemKey = (it) => {
  const filename = it?.source?.filename;
  const ts = it?.timestamp_epoch ?? it?.timestamp;
  const t = it?.type;
  return `${filename || 'item'}-${ts}-${t || 'event'}`;
};

const matchesFilter = (item, filterId) => {
  const t = String(item?.type || '').toLowerCase();
  if (filterId === 'all') return true;
  if (filterId === 'incident') return t === 'incident';
  if (filterId === 'troubleshoot') return t === 'troubleshoot';
  if (filterId === 'unban') return t === 'unban';
  if (filterId === 'config') return t === 'config_snapshot' || t === 'config_rollback';
  return true;
};

const buildSearchText = (item, markdownContent) => {
  const base = [
    item?.type,
    item?.shorttext,
    item?.timestamp,
    item?.source?.filename,
    item?.source?.format,
  ]
    .filter(Boolean)
    .join(' ');

  const details = item?.details ? JSON.stringify(item.details) : '';
  const md = typeof markdownContent === 'string' ? markdownContent : '';
  return (base + ' ' + details + ' ' + md).toLowerCase();
};

export default function IncidentTimelinePage() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [filterId, setFilterId] = useState('all');
  const [query, setQuery] = useState('');
  const [openKeys, setOpenKeys] = useState([]);
  const [fileCache, setFileCache] = useState({});
  const lastPrefetchQueryRef = useRef('');

  const fetchTimeline = async () => {
    const isRefresh = !loading;
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get('/incidents/timeline');
      setItems(res.data?.items || []);
    } catch {
      toast.error('Incident-Timeline konnte nicht geladen werden');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const ensureFileLoaded = async (filename) => {
    if (!filename) return;
    const cur = fileCache[filename];
    if (cur?.status === 'loading' || cur?.status === 'success') return;

    setFileCache((prev) => ({
      ...prev,
      [filename]: { status: 'loading', content: '', error: '' },
    }));

    try {
      const api = authAxios();
      const res = await api.get(`/incidents/timeline/${encodeURIComponent(filename)}`);
      setFileCache((prev) => ({
        ...prev,
        [filename]: { status: 'success', content: res.data?.content || '', error: '' },
      }));
    } catch {
      setFileCache((prev) => ({
        ...prev,
        [filename]: { status: 'error', content: '', error: 'Log-Datei konnte nicht geladen werden' },
      }));
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, []); // eslint-disable-line

  useEffect(() => {
    const opened = new Set(openKeys);
    const toLoad = [];
    for (const it of items) {
      const filename = it?.source?.filename;
      const format = it?.source?.format;
      const key = itemKey(it);
      if (!opened.has(key)) continue;
      if (format !== 'markdown') continue;
      if (!filename) continue;
      const cached = fileCache[filename];
      if (cached?.status === 'success' || cached?.status === 'loading') continue;
      toLoad.push(filename);
    }
    if (toLoad.length === 0) return;
    toLoad.slice(0, 6).forEach((fn) => ensureFileLoaded(fn));
  }, [openKeys, items]); // eslint-disable-line

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      lastPrefetchQueryRef.current = '';
      return;
    }
    if (lastPrefetchQueryRef.current === q) return;
    lastPrefetchQueryRef.current = q;

    const candidates = items
      .filter((it) => matchesFilter(it, filterId))
      .filter((it) => it?.source?.format === 'markdown')
      .map((it) => it?.source?.filename)
      .filter(Boolean);

    const uniq = Array.from(new Set(candidates));
    const toPrefetch = uniq.filter((fn) => fileCache[fn]?.status !== 'success').slice(0, 20);
    if (toPrefetch.length === 0) return;
    toPrefetch.forEach((fn) => ensureFileLoaded(fn));
  }, [query, filterId, items]); // eslint-disable-line

  const viewItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = items.filter((it) => matchesFilter(it, filterId));
    if (!q) return base;
    return base.filter((it) => {
      const fn = it?.source?.filename;
      const md = fn && fileCache[fn]?.status === 'success' ? fileCache[fn]?.content : '';
      return buildSearchText(it, md).includes(q);
    });
  }, [items, filterId, query, fileCache]);

  return (
    <div className="space-y-6" data-testid="incident-timeline-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">Incident-Timeline</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">Neueste Einträge zuerst</p>
        </div>
        <Button
          onClick={fetchTimeline}
          disabled={refreshing}
          className="h-11 px-5 bg-white/5 hover:bg-white/10 text-white rounded-lg"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-3" data-testid="incident-timeline-filters">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <Button
              key={f.id}
              onClick={() => setFilterId(f.id)}
              className={`h-10 px-4 rounded-lg ${
                filterId === f.id
                  ? 'bg-[#EF694C] hover:bg-[#d65d43] text-white'
                  : 'bg-white/5 hover:bg-white/10 text-white'
              }`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche (Freitext über Kurztext und Details)…"
            className="h-10 pl-10 bg-[#121214] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
          />
        </div>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <div className="text-sm text-[#a1a1aa]">{viewItems.length} Einträge</div>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <RefreshCw className="w-7 h-7 text-[#EF694C] animate-spin" />
          </div>
        ) : viewItems.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#a1a1aa]">Keine Einträge gefunden.</div>
        ) : (
          <div className="p-4">
            <div className="relative pl-6">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-white/10" />
              <Accordion
                type="multiple"
                value={openKeys}
                onValueChange={setOpenKeys}
                className="space-y-3"
              >
                {viewItems.map((it, idx) => {
                  const badge = typeBadge(it.type);
                  const filename = it?.source?.filename;
                  const format = it?.source?.format;
                  const key = itemKey(it);
                  const markerColor = markerColorByType(it.type);

                  const mdState = filename ? fileCache[filename] : null;
                  const mdLoading = mdState?.status === 'loading';
                  const mdError = mdState?.status === 'error' ? mdState?.error : '';
                  const mdContent = mdState?.status === 'success' ? mdState?.content : '';

                  return (
                    <AccordionItem key={key} value={key} className="border-0">
                      <div className="relative">
                        <div className={`absolute left-[0.55rem] top-6 w-3 h-3 rounded-full ${markerColor} ring-4 ring-[#121214] border border-white/10`} />
                        <AccordionTrigger className="px-4 py-4 rounded-lg border border-white/10 bg-[#0c0c0e]/40 hover:bg-[#0c0c0e]/60 hover:no-underline">
                          <div className="ml-6 flex items-start gap-3 text-left w-full">
                            <div className="pt-0.5">
                              <Badge className={badge.cls}>{badge.label}</Badge>
                            </div>
                            <div className="flex-1">
                              <div className="text-white font-medium leading-snug">{it.shorttext || '(ohne Titel)'}</div>
                              <div className="mt-1 text-xs text-[#a1a1aa] font-mono">{formatGermanDateTime(it.timestamp)}</div>
                            </div>
                            {filename ? (
                              <div className="text-xs text-[#a1a1aa] font-mono hidden md:block">{filename}</div>
                            ) : null}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="ml-6">
                          <div className="px-4 pb-4">
                            {format === 'markdown' ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="text-xs text-[#a1a1aa]">Incident-Log (Markdown)</div>
                                  {filename ? (
                                    <Button
                                      onClick={() => ensureFileLoaded(filename)}
                                      disabled={mdLoading}
                                      className="h-8 px-3 bg-white/5 hover:bg-white/10 text-white rounded-md"
                                    >
                                      <RefreshCw className={`w-4 h-4 mr-2 ${mdLoading ? 'animate-spin' : ''}`} />
                                      Laden
                                    </Button>
                                  ) : null}
                                </div>
                                {mdError ? (
                                  <div className="text-sm text-red-300">{mdError}</div>
                                ) : mdLoading ? (
                                  <div className="flex items-center gap-2 text-sm text-[#a1a1aa]">
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Lade Log…
                                  </div>
                                ) : (
                                  <pre className="text-xs leading-relaxed whitespace-pre-wrap bg-black/30 border border-white/10 rounded-lg p-3 overflow-x-auto text-[#EAF0FF]">{mdContent}</pre>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <div className="text-xs text-[#a1a1aa]">Details (JSON)</div>
                                <pre className="text-xs leading-relaxed bg-black/30 border border-white/10 rounded-lg p-3 overflow-x-auto text-[#EAF0FF]">{JSON.stringify(it.details || {}, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </div>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
